import React, { useRef, useState } from 'react';
import { Upload, File as FileIcon, X, Loader2 } from 'lucide-react';
import { getApiUrl } from '../lib/api';
import { toast } from 'sonner';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx';
const MAX_SIZE = 20 * 1024 * 1024;
const MAX_FILES = 10;

const formatSize = (b) =>
  b < 1024 * 1024 ? `${Math.round(b / 1024)} Ko` : `${(b / 1048576).toFixed(1)} Mo`;

export const FileUpload = ({ files, onChange, testId = 'file-upload' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = '';
    if (!selected.length) return;
    if (files.length + selected.length > MAX_FILES) {
      toast.error(`Vous pouvez joindre ${MAX_FILES} fichiers maximum.`);
      return;
    }
    setUploading(true);
    const uploaded = [];
    for (const f of selected) {
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name} dépasse la limite de 20 Mo.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', f);
        const res = await fetch(`${getApiUrl()}/api/uploads`, { method: 'POST', body: fd });
        if (res.ok) {
          const d = await res.json();
          uploaded.push({ id: d.id, filename: d.filename, size: d.size });
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || `Échec de l'envoi de ${f.name}.`);
        }
      } catch {
        toast.error(`Erreur réseau lors de l'envoi de ${f.name}.`);
      }
    }
    if (uploaded.length) onChange([...files, ...uploaded]);
    setUploading(false);
  };

  const remove = (id) => onChange(files.filter((f) => f.id !== id));

  return (
    <div className="space-y-3" data-testid={testId}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`${testId}-trigger`}
        className="w-full flex items-center justify-center gap-3 border-2 border-dashed border-slate-300 hover:border-emerald-500 rounded-xl py-6 px-4 text-slate-600 hover:text-emerald-700 transition-colors bg-slate-50/60"
      >
        {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
        <span className="text-sm font-medium text-center">
          {uploading ? 'Envoi en cours…' : 'Joindre plans, photos, CCTP, DPGF, devis…'}
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        onChange={handleSelect}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <p className="text-xs text-slate-400">
        PDF, JPG, PNG, DOC/DOCX, XLS/XLSX — 20 Mo max par fichier, 10 fichiers maximum.
      </p>
      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((f) => (
            <li
              key={f.id}
              data-testid={`${testId}-item`}
              className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileIcon className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm text-slate-700 truncate">{f.filename}</span>
                <span className="text-xs text-slate-400 flex-shrink-0">{formatSize(f.size)}</span>
              </div>
              <button
                type="button"
                onClick={() => remove(f.id)}
                className="text-slate-400 hover:text-red-500 transition-colors"
                data-testid={`${testId}-remove`}
                aria-label="Retirer le fichier"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileUpload;
